// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { xIsAuthenticated } from "@conklin/App/Store/Selectors/GeneralSelectors";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@conklin/Overrides/Components/ProductAddToListLink";
import ProductQuantityOrdered, { ProductQuantityStyles } from "@conklin/Overrides/Components/ProductQuantityOrdered";
import { mergeWithCss, parseBoolean } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as React from "react";
import { FC, useState } from "react";
import { connect } from "react-redux";

export interface ProductActionsProps {
    carouselId?: string;
    iconOnly?: boolean | null;
    showQty?: boolean;
    extendedStyles?: ProductActionsStyle;
    onAddToCartClick?: () => void;
    currentPage?: string;
    showDisabled?: boolean;
    hideUOM?: boolean;
    showAddToList?: boolean;
    secure?: boolean;
    // XNG-Change
    isPdp?: boolean;
}
type Props = ProductActionsProps & HasProductContext & ReturnType<typeof mapStateToProps>;
export interface ProductActionsStyle {
    addToCartContainer?: GridContainerProps;
    qtyWrapper?: GridItemProps;
    addToCartButtonWrapper?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    qtyField?: ProductQuantityStyles;
    addToListLink?: ProductAddToListLinkStyles;
}
export const productActionsStyle: ProductActionsStyle = {};
const mapStateToProps = (state: ApplicationState) => ({
    currentPage: getCurrentPage(state)?.type,
    isAuthenticated: xIsAuthenticated(state),
    configurationCompleted: state.pages.productDetails.configurationCompleted,
    variantSelectionCompleted: state.pages.productDetails.variantSelectionCompleted,
});
const ProductActions: FC<Props> = ({
    productContext,
    carouselId,
    iconOnly,
    extendedStyles,
    onAddToCartClick,
    hideUOM,
    isAuthenticated,
    secure,
    showQty,
    showAddToList = false,
    // XNG-Change
    isPdp,
    configurationCompleted,
    variantSelectionCompleted,
}) => {
    const history = useHistory();
    if (secure && !isAuthenticated) {
        return null;
    }
    const [styles] = useState(() => mergeWithCss(productActionsStyle, extendedStyles));
    const {
        product: { isVariantParent, quoteRequired, properties, canAddToWishlist },
    } = productContext;

    const handleVariantSelect = () => {
        history.push(productContext.productInfo.productDetailPath);
    };
    return (
        <StyledWrapper {...styles.addToCartContainer} data-test-selector="product_actions">
            {!(isPdp && (quoteRequired || (!isAuthenticated && parseBoolean(properties.authRequiredForPurchase)))) && (
                <>
                    {showQty && (
                        <StyledWrapper {...styles.qtyWrapper}>
                            <VisuallyHidden as="label" htmlFor={`${carouselId}-qty`} id={`${carouselId}-qty-label`}>
                                {translate("QTY")}
                            </VisuallyHidden>
                            <ProductQuantityOrdered
                                data-test-selector="productQty"
                                labelOverride=""
                                extendedStyles={styles.qtyField}
                                isPdp={isPdp}
                            />
                        </StyledWrapper>
                    )}
                    {!hideUOM && (
                        <ProductUnitOfMeasureSelect
                            labelOverride=""
                            extendedStyles={styles.productUnitOfMeasureSelectStyles}
                            id={`${carouselId}-uom`}
                            disabled={isPdp && isVariantParent && !variantSelectionCompleted}
                        />
                    )}
                </>
            )}
            <StyledWrapper {...styles.addToCartButtonWrapper}>
                {!isVariantParent ? (
                    <ProductAddToCartButton
                        {...{ isIconButton: iconOnly }}
                        onAddToCartClick={onAddToCartClick}
                        extendedStyles={styles.productAddToCartButtonStyles}
                        data-test-selector="addToCartBtn"
                        {...{ isPdp }}
                        {...(isPdp && {
                            configurationCompleted,
                            variantSelectionCompleted,
                        })}
                    />
                ) : (
                    <Button
                        {...styles.productAddToCartButtonStyles}
                        disabled={isPdp && isVariantParent && !variantSelectionCompleted}
                        onClick={handleVariantSelect}
                    >
                        {isPdp ? translate("Add to Cart") : translate("Choose Selection")}
                    </Button>
                )}
            </StyledWrapper>

            {showAddToList && canAddToWishlist && (
                <ProductAddToListLink
                    data-test-selector="productDetails_addToList"
                    extendedStyles={styles.addToListLink}
                    type="button"
                />
            )}
        </StyledWrapper>
    );
};
export default connect(mapStateToProps)(withProductContext(ProductActions));
